import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import DateRangePicker from '../components/DateRangePicker';
import InfoPanel from '../components/InfoPanel';
import { useDateRange } from '../hooks/useDateRange';
import { toCsv, downloadCsv, rangeSuffix } from '../utils/csv';
import type { UserViewCount } from '../types';

// Each row is one user, so counting rows gives distinct user counts directly.
function platformOf(user: UserViewCount): string {
  const app = Number(user.app_view_count) > 0;
  const web = Number(user.web_view_count) > 0;
  if (app && web) return 'App + Web';
  if (app) return 'App';
  if (web) return 'Web';
  if (Number(user.compass_view_count) > 0) return 'Compass';
  return 'Not recorded';
}

const PLATFORM_STYLES: Record<string, string> = {
  'App + Web': 'bg-violet-100 text-violet-800',
  App: 'bg-blue-100 text-blue-800',
  Web: 'bg-emerald-100 text-emerald-800',
  Compass: 'bg-gray-100 text-gray-700',
  'Not recorded': 'bg-amber-100 text-amber-800',
};

export default function UserViews() {
  const { fromDate, toDate, setFromDate, setToDate, from, to } = useDateRange();
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');

  const { data: allUsers, isLoading } = useQuery({
    queryKey: ['allUsers', from, to],
    queryFn: () => api.getAllUsers(from, to),
  });

  const filteredUsers = useMemo(() => {
    let users = allUsers?.users ?? [];
    if (searchTerm) {
      users = users.filter((user) =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (platformFilter === 'app') {
      users = users.filter((u) => Number(u.app_view_count) > 0);
    } else if (platformFilter === 'web') {
      users = users.filter((u) => Number(u.web_view_count) > 0);
    } else if (platformFilter === 'both') {
      users = users.filter(
        (u) => Number(u.app_view_count) > 0 && Number(u.web_view_count) > 0
      );
    }
    return users;
  }, [allUsers, searchTerm, platformFilter]);

  // Counted across all users, not the filtered set, so the headline activity
  // figures do not move while searching.
  const stats = useMemo(() => {
    const users = allUsers?.users ?? [];
    return {
      totalUsers: users.length,
      totalViews: users.reduce((sum, u) => sum + Number(u.view_count), 0),
      appUsers: users.filter((u) => Number(u.app_view_count) > 0).length,
      webUsers: users.filter((u) => Number(u.web_view_count) > 0).length,
      bothUsers: users.filter(
        (u) => Number(u.app_view_count) > 0 && Number(u.web_view_count) > 0
      ).length,
      unattributedUsers: users.filter(
        (u) =>
          Number(u.app_view_count) === 0 &&
          Number(u.web_view_count) === 0 &&
          Number(u.unattributed_view_count) > 0
      ).length,
    };
  }, [allUsers]);

  const num = (v: number | string) => Number(v).toLocaleString();

  const handleExport = () => {
    const headers = [
      'Email',
      'Platform',
      'App views',
      'Web views',
      'Compass views',
      'Views with no channel',
      'Total views',
    ];
    const rows = filteredUsers.map((u) => [
      u.email,
      platformOf(u),
      Number(u.app_view_count),
      Number(u.web_view_count),
      Number(u.compass_view_count),
      Number(u.unattributed_view_count),
      Number(u.view_count),
    ]);
    downloadCsv(
      `imray-users-by-platform_${rangeSuffix(fromDate, toDate)}.csv`,
      toCsv(headers, rows)
    );
  };

  return (
    <div className="space-y-6">
      <InfoPanel id="users" title="Users Overview">
        All users ranked by content engagement, showing which platform each one used.
        A user counts as active on a platform if they viewed any content there during the
        selected period. Users active on both are counted in both figures, so the app and
        web totals overlap and will not sum to the total user count. Excluded users
        (internal staff) are filtered out automatically based on admin settings.
      </InfoPanel>

      <div className="flex justify-between items-start">
        <h1 className="text-2xl font-bold text-gray-900">Users by Platform</h1>
        <DateRangePicker
          from={fromDate}
          to={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Active on app</div>
          <div className="text-2xl font-bold text-gray-900">{num(stats.appUsers)}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Active on web</div>
          <div className="text-2xl font-bold text-gray-900">{num(stats.webUsers)}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Used both</div>
          <div className="text-2xl font-bold text-gray-900">{num(stats.bothUsers)}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Total users</div>
          <div className="text-2xl font-bold text-gray-900">{num(stats.totalUsers)}</div>
        </div>
      </div>

      <div className="card border-l-4 border-sky-400 bg-sky-50">
        <div className="text-sm text-sky-900">
          <strong>Active on app is not the subscriber count.</strong> It counts people who
          read something in the app during this period, which includes trial and lapsed
          users and excludes subscribers who did not open the app. The £0.99 figure comes
          from Stripe and App Store Connect.
        </div>
      </div>

      {stats.unattributedUsers > 0 && (
        <div className="card border-l-4 border-amber-400 bg-amber-50">
          <div className="text-sm text-amber-900">
            <strong>{num(stats.unattributedUsers)}</strong> users have no platform recorded
            for any of their views in this range. Device tracking began on 2 December 2024;
            earlier activity cannot be attributed to app or web.
          </div>
        </div>
      )}

      <div className="card">
        <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search users by email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input max-w-md"
            />
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="input"
            >
              <option value="all">All platforms</option>
              <option value="app">App users</option>
              <option value="web">Web users</option>
              <option value="both">Used both</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={filteredUsers.length === 0}
            className="px-4 py-2 text-sm font-medium rounded-md bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Export CSV
          </button>
        </div>
        <div className="text-sm text-gray-500 mb-2">
          Showing {num(filteredUsers.length)} of {num(stats.totalUsers)} users
          {' · '}
          {num(stats.totalViews)} views in total
        </div>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="animate-pulse h-8 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : (
          <div className="table-container max-h-[600px] overflow-y-auto">
            <table className="table">
              <thead className="sticky top-0">
                <tr>
                  <th>#</th>
                  <th>Email</th>
                  <th>Platform</th>
                  <th className="text-right">App views</th>
                  <th className="text-right">Web views</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.map((user, index) => {
                  const platform = platformOf(user);
                  return (
                    <tr key={index}>
                      <td className="text-gray-500">{index + 1}</td>
                      <td>{user.email}</td>
                      <td>
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${PLATFORM_STYLES[platform]}`}
                        >
                          {platform}
                        </span>
                      </td>
                      <td className="text-right font-medium">{num(user.app_view_count)}</td>
                      <td className="text-right font-medium">{num(user.web_view_count)}</td>
                      <td className="text-right font-medium">{num(user.view_count)}</td>
                      <td className="text-right text-gray-500">
                        {stats.totalViews > 0
                          ? `${((Number(user.view_count) / stats.totalViews) * 100).toFixed(2)}%`
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
